// Kling 4.0: current channel capabilities (4K is not available yet).
export const KLING_V4_SIZES = {
  '16:9': { '720p': '1280x720', '1080p': '1920x1080' },
  '9:16': { '720p': '720x1280', '1080p': '1080x1920' },
  '1:1': { '720p': '720x720', '1080p': '1080x1080' },
  '21:9': { '720p': '1680x720', '1080p': '2520x1080' },
  adaptive: { '720p': 'adaptive_720p', '1080p': 'adaptive_1080p' },
};

export const KLING_V4_CAPABILITIES = {
  allowedAspectRatios: Object.keys(KLING_V4_SIZES),
  allowedResolutions: ['720p', '1080p'],
  allowedSizesByAspect: KLING_V4_SIZES,
  allowedDurations: Array.from({ length: 28 }, (_, index) => index + 3),
  defaultDuration: 5,
  minDurationSeconds: 3,
  maxDurationSeconds: 30,
  maxResolution: '1080p',
  allowedInputModes: ['text', 'image', 'video', 'audio'],
  allowedVideoFunctionModes: ['general', 'reference', 'frames', 'editing', 'extend'],
  maxInputImages: 10,
  maxInputVideos: 5,
  maxInputAudios: 3,
  maxInputAssets: 15,
  maxInputVideoDurationSeconds: 30,
  maxInputVideoTotalDurationSeconds: 30,
  maxInputAudioDurationSeconds: 30,
  supportsNativeAudio: true,
};

export const KLING_V4_CREDITS_PER_SECOND = {
  noVideo: { '720p': 58, '1080p': 88 },
  withVideo: { '720p': 35, '1080p': 52 },
};

export const getKlingV4CreditCost = ({
  resolution = '720p', duration = 5, hasVideoReference = false, inputVideoDuration = 0,
} = {}) => {
  const normalized = String(resolution).toLowerCase();
  const label = normalized.includes('1080') ? '1080p' : normalized.includes('720') ? '720p' : null;
  const seconds = Number(duration);
  if (!label || !Number.isInteger(seconds) || seconds < 3 || seconds > 30) {
    throw new Error('Kling 4.0 requires 720p or 1080p and an integer duration from 3 to 30 seconds.');
  }
  const inputSeconds = Math.ceil(Math.max(0, Number(inputVideoDuration) || 0));
  const withVideo = hasVideoReference || inputSeconds > 0;
  return withVideo
    ? KLING_V4_CREDITS_PER_SECOND.withVideo[label] * (seconds + Math.max(seconds, inputSeconds))
    : KLING_V4_CREDITS_PER_SECOND.noVideo[label] * seconds;
};

export const getKlingV4InputDuration = (assets = []) => assets.filter((asset) => asset?.type === 'video')
  .reduce((sum, asset) => sum + Math.max(0, Number(asset.durationSeconds ?? asset.duration) || 0), 0);

export const getKlingV4MaxFrames = (duration = 5) => Math.min(10, Number(duration) + 1);

// Shared by the browser and API, before credit checks or vendor submission.
export const validateKlingV4Inputs = (assets = [], { functionMode = 'general', duration = 5 } = {}) => {
  const images = assets.filter((asset) => asset.type === 'image');
  const videos = assets.filter((asset) => asset.type === 'video');
  const audios = assets.filter((asset) => asset.type === 'audio');
  const error = (errorKey, values = {}) => ({ errorKey, values });
  if (!Number.isInteger(Number(duration)) || Number(duration) < 3 || Number(duration) > 30) return error('klingV4Duration');
  if (assets.length > 15 || images.length > 10 || videos.length > 5 || audios.length > 3) return error('klingV4AssetCount');
  if (functionMode === 'general' && (images.length > 1 || videos.length || audios.length)) return error('klingV4General');
  if (functionMode === 'frames' && (images.length < 1 || images.length > getKlingV4MaxFrames(duration) || videos.length || audios.length)) {
    return error('klingV4Frames', { count: getKlingV4MaxFrames(duration) });
  }
  if (['editing', 'extend'].includes(functionMode) && (videos.length !== 1 || audios.length || (functionMode === 'extend' && images.length > 1))) return error('klingV4Source');
  for (const asset of [...videos, ...audios]) {
    const seconds = Number(asset.durationSeconds);
    const min = asset.type === 'audio' ? 5 : functionMode === 'reference' ? 2 : 3;
    if (!Number.isFinite(seconds) || seconds < min || seconds > 30) return error('klingV4MediaDuration', { min, max: 30 });
  }
  if (videos.reduce((sum, asset) => sum + Number(asset.durationSeconds), 0) > 30) return error('klingV4VideoTotal');
  return null;
};
